import { sanitizeAttachments, sanitizeEnvVars } from '@reflex/ui/client/agent-config-utils';
import { buildContentBlocks, resolveSandboxOptions } from '@runloop/reflex-contract';
import type { AgentDraftPayload } from '@reflex/shared';
import type { Attachment } from '@/lib/types';
import type { LaunchFormState } from './form-state';

export function launchFormToDraftPayload(
  state: LaunchFormState,
  selectedBlueprintName: string | null,
): AgentDraftPayload {
  const selectedAttachments = sanitizeAttachments(state.attachmentValues);
  const sanitizedEnvVars = sanitizeEnvVars(state.envVars);
  const inlineAttachments = state.chatAttachments.filter(
    (a) => (a.type === 'image' || a.type === 'file') && a.data,
  );
  const content =
    inlineAttachments.length > 0
      ? buildContentBlocks(state.initialPrompt, state.chatAttachments)
      : undefined;

  const sandboxOverrides = {
    ...(state.resourceSize ? { resourceSize: state.resourceSize } : {}),
    ...(state.customSize ? { customSize: state.customSize } : {}),
    ...(state.dockerd ? { dockerd: true } : {}),
    ...(state.computerUse ? { computerUse: true } : {}),
    ...(state.idleTimeMinutes ? { idleTimeMinutes: state.idleTimeMinutes } : {}),
    ...(state.snapshotId ? { snapshotId: state.snapshotId } : {}),
  };
  const sandboxOptions = resolveSandboxOptions({
    blueprintName: state.snapshotId ? null : selectedBlueprintName,
    ...(Object.keys(sandboxOverrides).length ? { sandboxOptions: sandboxOverrides } : {}),
  });

  return {
    agentType: state.agentType,
    prompt: state.initialPrompt || undefined,
    initialPrompt: state.initialPrompt,
    model: state.model || undefined,
    modelEndpointId: state.modelEndpointId ?? undefined,
    systemPrompt: state.systemPrompt ?? undefined,
    blueprintName: selectedBlueprintName && !state.snapshotId ? selectedBlueprintName : undefined,
    manualBlueprintName: state.manualBlueprintName,
    sandboxOptions: sandboxOptions ?? undefined,
    snapshotId: state.snapshotId,
    resourceSize: state.resourceSize,
    dockerd: state.dockerd,
    computerUse: state.computerUse,
    idleTimeMinutes: state.idleTimeMinutes,
    content,
    chatAttachments: state.chatAttachments,
    attachments: selectedAttachments.length ? selectedAttachments : undefined,
    attachmentValues: state.attachmentValues,
    envVars: sanitizedEnvVars.length ? sanitizedEnvVars : undefined,
    labelIds: state.labelIds.length ? state.labelIds : undefined,
    groupId: state.groupId ?? undefined,
    providerSecretId: state.selectedProviderSecretId ?? undefined,
    selectedProviderId: state.selectedProviderId,
    selectedPersonaId: state.selectedPersonaId,
    reasoningEffort: state.reasoningEffort,
    promptMode: state.promptMode ?? undefined,
  };
}

export function draftPayloadToPersistedData(payload: AgentDraftPayload) {
  return {
    agentType: payload.agentType!,
    selectedProviderId: payload.selectedProviderId ?? null,
    model: payload.model ?? '',
    modelEndpointId: payload.modelEndpointId ?? null,
    manualBlueprintName: payload.manualBlueprintName ?? payload.blueprintName ?? null,
    snapshotId: payload.snapshotId ?? payload.sandboxOptions?.snapshotId ?? null,
    resourceSize: payload.resourceSize ?? payload.sandboxOptions?.resourceSize ?? null,
    customSize: payload.sandboxOptions?.customSize ?? null,
    dockerd: payload.dockerd ?? payload.sandboxOptions?.dockerd ?? false,
    computerUse: payload.computerUse ?? payload.sandboxOptions?.computerUse ?? false,
    idleTimeMinutes: payload.idleTimeMinutes ?? payload.sandboxOptions?.idleTimeMinutes ?? null,
    attachmentValues: payload.attachmentValues ?? {},
    envVars: payload.envVars ?? [],
    labelIds: payload.labelIds ?? [],
    groupId: payload.groupId ?? null,
    initialPrompt: payload.initialPrompt ?? payload.prompt ?? '',
    selectedPersonaId: payload.selectedPersonaId ?? null,
    systemPrompt: payload.systemPrompt ?? null,
    chatAttachments: (payload.chatAttachments ?? []) as Attachment[],
    selectedProviderSecretId: payload.providerSecretId ?? null,
    reasoningEffort: payload.reasoningEffort ?? null,
    promptMode: payload.promptMode ?? null,
  };
}

/** True when the draft has no user-authored message content worth persisting. */
export function isDraftContentEmpty(payload: AgentDraftPayload): boolean {
  const prompt = (payload.initialPrompt ?? payload.prompt ?? '').trim();
  const attachments = payload.chatAttachments ?? [];
  return prompt === '' && attachments.length === 0;
}

export function isLaunchFormDirty(state: LaunchFormState, baseline: LaunchFormState): boolean {
  return (
    state.agentType !== baseline.agentType ||
    state.selectedProviderId !== baseline.selectedProviderId ||
    state.model !== baseline.model ||
    state.modelEndpointId !== baseline.modelEndpointId ||
    state.manualBlueprintName !== baseline.manualBlueprintName ||
    state.snapshotId !== baseline.snapshotId ||
    state.resourceSize !== baseline.resourceSize ||
    (state.customSize?.name ?? null) !== (baseline.customSize?.name ?? null) ||
    state.dockerd !== baseline.dockerd ||
    state.computerUse !== baseline.computerUse ||
    state.idleTimeMinutes !== baseline.idleTimeMinutes ||
    state.selectedPersonaId !== baseline.selectedPersonaId ||
    state.systemPrompt !== baseline.systemPrompt ||
    state.initialPrompt !== baseline.initialPrompt ||
    state.selectedProviderSecretId !== baseline.selectedProviderSecretId ||
    state.reasoningEffort !== baseline.reasoningEffort ||
    state.promptMode !== baseline.promptMode ||
    state.groupId !== baseline.groupId ||
    state.labelIds.join(',') !== baseline.labelIds.join(',') ||
    state.chatAttachments.length !== baseline.chatAttachments.length ||
    JSON.stringify(state.attachmentValues) !== JSON.stringify(baseline.attachmentValues) ||
    JSON.stringify(state.envVars) !== JSON.stringify(baseline.envVars)
  );
}
